<template>
  <div class="map-sidebar">
    <!-- Top toggle: Alertas | Monitoreo -->
    <div class="sidebar-toggle-group">
      <button
        :class="['toggle-btn', { 'toggle-btn--active': activeTab === 0 }]"
        @click="activeTab = 0"
      >
        <v-icon x-small class="mr-1">mdi-bell-alert</v-icon>
        Alertas
        <v-chip
          v-if="alerts.length > 0"
          x-small
          color="error"
          class="ml-1 toggle-badge"
        >{{ alerts.length }}</v-chip>
      </button>
      <button
        :class="['toggle-btn', { 'toggle-btn--active': activeTab === 1 }]"
        @click="activeTab = 1"
      >
        <v-icon x-small class="mr-1">mdi-map-marker-multiple</v-icon>
        Monitoreo
      </button>
    </div>

    <!-- Monitoreo content -->
    <template v-if="activeTab === 1">
      <!-- Entity type tabs: 3 inline + chip "+N" con dropdown al resto -->
      <div class="entity-tabs">
        <button
          v-for="slot in visibleEntityTabs"
          :key="slot.type"
          :class="['entity-tab', { 'entity-tab--active': selectedEntity === slot.type }]"
          :style="getEntityTabStyle(slot.type)"
          @click="selectEntity(slot.type)"
        >
          <v-icon x-small :color="slot.cfg.color">{{ slot.cfg.mdiIcon }}</v-icon>
          <span class="entity-tab-count">{{ viewportCounts[slot.type] || 0 }}</span>
        </button>
        <v-menu
          v-if="overflowEntityTabs.length > 0"
          v-model="entityOverflowMenu"
          offset-y
          left
          :close-on-content-click="true"
          content-class="entity-overflow-menu"
          :nudge-bottom="4"
        >
          <template #activator="{ on, attrs }">
            <button
              class="entity-tab entity-tab--more"
              v-bind="attrs"
              v-on="on"
              :title="'Ver ' + overflowEntityTabs.length + ' tipo' + (overflowEntityTabs.length === 1 ? '' : 's') + ' mas'"
            >
              <v-icon x-small>mdi-dots-horizontal</v-icon>
              <span class="entity-tab-count entity-tab-count--more">+{{ overflowEntityTabs.length }}</span>
            </button>
          </template>
          <div class="entity-overflow-menu__list">
            <div
              v-for="slot in overflowEntityTabs"
              :key="slot.type"
              class="entity-overflow-menu__item"
              @click="onEntityOverflowSelect(slot.type)"
            >
              <v-icon x-small :color="slot.cfg.color" class="mr-2">{{ slot.cfg.mdiIcon }}</v-icon>
              <span class="entity-overflow-menu__label">{{ slot.cfg.label }}</span>
              <span class="entity-overflow-menu__count">{{ viewportCounts[slot.type] || 0 }}</span>
            </div>
          </div>
        </v-menu>
      </div>

      <!-- Selected entity header + stats + filters -->
      <div v-if="selectedEntity" class="entity-header">
        <div class="entity-header-top">
          <span class="entity-header-name">{{ entityConfigs[selectedEntity].label }}</span>
          <!-- Conteo por conexion SOLO donde ese estado existe. Para el resto
               (incidentes, reportantes, sismos, presas...) se muestra el total:
               antes el contador tomaba todo lo que no fuera 'online' como
               "fuera de linea", asi que un incidente ACTIVO se presentaba como
               desconectado. Ver hasConnectionStatus en entity-configs.js. -->
          <span v-if="showConnectionStatus" class="entity-header-counts">
            <span class="stats-dot stats-dot--online"></span>{{ entityStatusCounts.online }}
            <span class="stats-dot stats-dot--offline ml-1"></span>{{ entityStatusCounts.offline }}
          </span>
          <span v-else class="entity-header-counts">{{ entityStatusCounts.total }}</span>
          <!-- Filter dropdown + viewport toggle -->
          <div class="entity-header-filters">
            <v-menu v-if="showConnectionStatus" offset-y :close-on-content-click="true" bottom>
              <template #activator="{ on }">
                <button :class="['filter-dropdown', activeFilterClass]" v-on="on">
                  <v-icon x-small :color="activeFilterColor">mdi-filter-variant</v-icon>
                  <span class="filter-dropdown-text">{{ activeFilterLabel }}</span>
                  <v-icon x-small class="filter-dropdown-arrow">mdi-chevron-down</v-icon>
                </button>
              </template>
              <div class="filter-menu">
                <button v-for="opt in filterOptions" :key="opt.value" class="filter-menu-item" :class="{ 'filter-menu-item--active': currentFilter === opt.value }" @click="setFilter(opt.value)">
                  <v-icon x-small :color="opt.color" class="mr-1">{{ opt.icon }}</v-icon>
                  {{ opt.label }}
                </button>
              </div>
            </v-menu>
            <button :class="['viewport-toggle', { 'viewport-toggle--active': viewportOnly }]" v-auto-tooltip="viewportOnly ? 'Mostrar todos' : 'Solo los visibles en el mapa'" @click="viewportOnly = !viewportOnly">
              <v-icon x-small>mdi-map-marker-radius</v-icon>
            </button>
          </div>
        </div>
        <!-- La barra proporcional representa conectados vs desconectados: sin
             estado de conexion no hay proporcion que mostrar, solo dos colores
             inventados. -->
        <div v-if="showConnectionStatus" class="stats-bar-visual">
          <div class="stats-fill stats-fill--online" :style="{ width: entityStatusCounts.onlinePct + '%' }"></div>
          <div class="stats-fill stats-fill--offline" :style="{ width: entityStatusCounts.offlinePct + '%' }"></div>
        </div>
      </div>

      <!-- Filter row (when NO entity selected) -->
      <div v-if="!selectedEntity" class="no-entity-filters">
        <v-menu offset-y :close-on-content-click="true" bottom>
          <template #activator="{ on }">
            <button :class="['filter-dropdown', activeFilterClass]" v-on="on">
              <v-icon x-small :color="activeFilterColor">mdi-filter-variant</v-icon>
              <span class="filter-dropdown-text">{{ activeFilterLabel }}</span>
              <v-icon x-small class="filter-dropdown-arrow">mdi-chevron-down</v-icon>
            </button>
          </template>
          <div class="filter-menu">
            <button v-for="opt in filterOptions" :key="opt.value" class="filter-menu-item" :class="{ 'filter-menu-item--active': currentFilter === opt.value }" @click="setFilter(opt.value)">
              <v-icon x-small :color="opt.color" class="mr-1">{{ opt.icon }}</v-icon>
              {{ opt.label }}
            </button>
          </div>
        </v-menu>
        <button :class="['viewport-toggle', { 'viewport-toggle--active': viewportOnly }]" v-auto-tooltip="viewportOnly ? 'Mostrar todos' : 'Solo los visibles en el mapa'" @click="viewportOnly = !viewportOnly">
          <v-icon x-small>mdi-map-marker-radius</v-icon>
        </button>
      </div>

      <!-- Search + sort row -->
      <div class="search-row">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          placeholder="Buscar..."
          prepend-inner-icon="mdi-magnify"
          clearable
          class="sidebar-search sidebar-search--compact"
        />
        <v-btn
          icon
          x-small
          class="sort-btn"
          :class="{ 'sort-btn--active': sortDesc }"
          v-auto-tooltip="'Ordenar'"
          @click="sortDesc = !sortDesc"
        >
          <v-icon small>{{ sortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
        </v-btn>
        <!-- Filtro por tipo: solo aparece en el tab de devices. Misma
             altura que el sort para mantener el ritmo de la fila. -->
        <v-menu
          v-if="selectedEntity === 'device'"
          offset-y
          bottom
          :close-on-content-click="true"
          :z-index="20"
        >
          <template #activator="{ on }">
            <v-btn
              icon
              x-small
              :class="['sort-btn', { 'sort-btn--active': !!deviceTypeFilter }]"
              v-auto-tooltip="deviceTypeFilter ? ('Filtro: ' + deviceTypeFilterLabel) : 'Filtrar por tipo'"
              v-on="on"
            >
              <v-icon small>mdi-filter-variant</v-icon>
            </v-btn>
          </template>
          <div class="filter-menu">
            <button
              type="button"
              class="filter-menu-item"
              :class="{ 'filter-menu-item--active': !deviceTypeFilter }"
              @click="deviceTypeFilter = null"
            >
              <v-icon x-small class="mr-1">mdi-checkbox-multiple-marked-outline</v-icon>
              Todos los tipos
            </button>
            <button
              v-for="opt in deviceTypeFilterOptions"
              :key="opt.slug"
              type="button"
              class="filter-menu-item"
              :class="{ 'filter-menu-item--active': deviceTypeFilter === opt.slug }"
              @click="deviceTypeFilter = opt.slug"
            >
              {{ opt.label }} <span class="filter-menu-item__count">({{ opt.count }})</span>
            </button>
          </div>
        </v-menu>
      </div>

      <!-- Monitor list (scrollable, fills remaining space) -->
      <div class="monitor-list">
        <div
          v-for="monitor in filteredMonitors"
          :key="monitor.id"
          :class="['monitor-item', { 'monitor-item--selected': monitor.id === selectedMonitorId }]"
          @click="$emit('select', monitor)"
        >
          <div class="monitor-info">
            <div class="monitor-title">{{ monitor.title }}</div>
            <div class="monitor-subtitle">{{ getSubtitleLine(monitor) }}</div>
            <div v-if="getDetailLine(monitor)" class="monitor-detail">{{ getDetailLine(monitor) }}</div>
          </div>
          <div class="monitor-right">
            <div class="monitor-time">{{ getRelativeTime(monitor) }}</div>
            <div
              class="monitor-status-dot"
              :class="'monitor-status-dot--' + monitor.status"
            />
          </div>
        </div>

        <div v-if="filteredMonitors.length === 0" class="monitor-empty">
          <v-icon small color="var(--kln-text-dim)">mdi-map-search</v-icon>
          <span>Sin elementos</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="sidebar-footer">
        <span>{{ filteredMonitors.length }}<span v-if="filteredMonitors.length >= maxItems">+</span> elementos</span>
      </div>
    </template>

    <!-- Alertas content -->
    <template v-if="activeTab === 0">
      <!-- Alert category tabs (2 filas flex). Cada fila ocupa el ancho
           completo del sidebar para que no queden huecos a la derecha
           cuando la fila inferior tiene menos items que la superior. -->
      <div class="alert-category-tabs">
        <div class="alert-category-tabs__row">
          <button
            v-for="cat in alertCategoriesRow1"
            :key="cat.key"
            :class="['alert-cat-btn', { 'alert-cat-btn--active': alertCategory === cat.key }]"
            @click="alertCategory = cat.key"
          >
            <v-icon x-small class="alert-cat-icon" :color="cat.color">{{ cat.icon }}</v-icon>
            <span class="alert-cat-label">{{ cat.label }}</span>
            <span class="alert-cat-badge">{{ alertCategoryCounts[cat.key] || 0 }}</span>
          </button>
        </div>
        <div v-if="alertCategoriesRow2.length" class="alert-category-tabs__row">
          <button
            v-for="cat in alertCategoriesRow2"
            :key="cat.key"
            :class="['alert-cat-btn', { 'alert-cat-btn--active': alertCategory === cat.key }]"
            @click="alertCategory = cat.key"
          >
            <v-icon x-small class="alert-cat-icon" :color="cat.color">{{ cat.icon }}</v-icon>
            <span class="alert-cat-label">{{ cat.label }}</span>
            <span class="alert-cat-badge">{{ alertCategoryCounts[cat.key] || 0 }}</span>
          </button>
        </div>
      </div>

      <!-- Search + filter button + sort button -->
      <div class="search-row">
        <v-text-field
          v-model="alertSearch"
          dense
          outlined
          hide-details
          placeholder="Buscar alertas..."
          prepend-inner-icon="mdi-magnify"
          clearable
          class="sidebar-search sidebar-search--compact"
        />
        <!-- Los filtros de prioridad y localidad solo existen para
             incidentes 911; el resto de categorías (SOS, alertas, hits,
             reportantes) no tienen esos campos. -->
        <v-menu v-if="alertCategory === '911'" offset-y :close-on-content-click="false" v-model="alertFilterMenu">
          <template #activator="{ on }">
            <v-btn icon x-small v-on="on" class="search-action-btn" :class="{ 'search-action-btn--active': hasActiveFilters }">
              <v-icon small>mdi-filter-variant</v-icon>
            </v-btn>
          </template>
          <div class="filter-popup">
            <div class="filter-popup__title">Filtros</div>
            <v-select
              v-model="alertFilterCategory"
              :items="categoryOptions"
              dense
              outlined
              hide-details
              clearable
              placeholder="Categoría"
              class="filter-popup__select"
            >
              <template #item="{ item }">
                <v-icon small class="mr-2">{{ item.icon }}</v-icon>
                <span>{{ item.text }}</span>
              </template>
              <template #selection="{ item }">
                <v-icon small class="mr-2">{{ item.icon }}</v-icon>
                <span>{{ item.text }}</span>
              </template>
            </v-select>
            <v-select
              v-model="alertFilterPriority"
              :items="priorityOptions"
              dense
              outlined
              hide-details
              clearable
              placeholder="Prioridad"
              class="filter-popup__select"
            />
            <v-select
              v-model="alertFilterLocation"
              :items="locationOptions"
              dense
              outlined
              hide-details
              clearable
              placeholder="Localidad"
              class="filter-popup__select"
            />
            <div class="filter-popup__actions">
              <a class="filter-popup__clear" @click="clearAlertFilters">Limpiar</a>
              <v-btn small color="var(--kln-accent)" @click="alertFilterMenu = false">Aplicar</v-btn>
            </div>
          </div>
        </v-menu>
        <!-- Toggle de prioridad — combina con el sort principal (time/folio).
             3 estados: inactivo → ALTA arriba → BAJA arriba → inactivo. -->
        <v-btn
          icon
          x-small
          class="search-action-btn"
          :class="{ 'search-action-btn--active': alertPriorityDir !== null }"
          @click="cyclePriorityDir"
          v-auto-tooltip="priorityDirTooltip"
        >
          <v-icon small>{{ priorityDirIcon }}</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          class="search-action-btn"
          :class="{ 'search-action-btn--active': alertSortBy.startsWith('folio') }"
          @click="cycleAlertSortBy"
          v-auto-tooltip="alertSortByTooltip"
        >
          <v-icon small>{{ alertSortByIcon }}</v-icon>
        </v-btn>
      </div>

      <!-- Alert list -->
      <div class="alert-list">
        <div
          v-for="alert in filteredAlerts"
          :key="alert.id || alert._id"
          :class="['alert-item', alertPriorityClass(alert), { 'alert-item--selected': isAlertSelected(alert) }]"
          @click="$emit('alert-click', alert)"
        >
          <!-- Line 1: Titulo especifico (nombre de la unidad/sensor) si existe;
               el subtitle es fallback para incidentes (tipo de incidente). Sin
               esto, alertas de panico de bus/sensor/device mostraban todas el
               mismo "Boton de Panico Activado" generico en lugar del nombre. -->
          <div class="alert-line1">
            <!-- Bandera del país detectado a partir del prefijo telefónico
                 del reportante (parsePhone() en util/phone-format.js). El
                 ISO se mapea a un emoji bandera vía Regional Indicator
                 Symbols, así que cualquier prefijo soportado obtiene su
                 bandera correcta sin mantener un mapping ISO → bitmap. -->
            <span
              v-if="alert.type === 'reportant' && alert.data && alert.data.phoneFlag"
              class="phone-flag"
              :aria-label="alert.data.phoneIso"
              :title="alert.data.phoneIso"
            >{{ alert.data.phoneFlag }}</span>
            <span class="alert-type-text">{{ alertPrimaryLabel(alert) }}</span>
          </div>
          <!-- Línea de folio (solo incidentes 9-1-1) — referencia operativa del
               caso, en su propia línea (estilo similar al teléfono de SOS). -->
          <div
            v-if="alert.type === 'incident' && alertFolio(alert)"
            class="alert-folio-line"
            :title="'Folio: ' + alertFolio(alert)"
          >
            <span class="alert-folio-value">{{ alertFolio(alert) }}</span>
          </div>
          <!-- Line 2: Icon (tipo del incidente, blanco neutro) + badge
               del nivel (outline coloreado) + location + tiempo relativo
               al final. Folio eliminado — el operador lo ve al abrir el
               drawer del incidente; ocupaba espacio sin valor operativo.
               Si la alerta proviene de una interconexión externa
               (Carbyne / DiDi / Uber / RapidSOS), el logo del proveedor
               aparece a la izquierda del icono de tipo para identificación
               instantánea — paridad con el legacy MapAlerts. -->
          <div class="alert-line2">
            <img
              v-if="alertPartner(alert)"
              :src="alertPartner(alert).logo"
              :alt="alertPartner(alert).label"
              :title="'Fuente: ' + alertPartner(alert).label"
              class="alert-partner-logo"
            />
            <!-- Incidente 911: chip combinado con icono + nombre de
                 categoría sobre fondo del color cat-<slug> del tema.
                 Reemplaza el badge ALTA/MEDIA/BAJA con outline (la
                 prioridad ya queda visible por la barra lateral coloreada
                 del item). Para alerts no-911 (sos, hit, sensor_alert,
                 etc.) se mantiene el icono v-icon original sin chip. -->
            <span
              v-if="isCnieIncident(alert)"
              class="alert-category-chip"
              :style="{ background: getIncidentColorVar(alert.data) }"
            >
              <v-icon x-small dark>{{ getIncidentMdiIcon(alert.data) }}</v-icon>
              <span class="alert-category-chip__label">{{ getIncidentCategoryLabel(alert.data) }}</span>
            </span>
            <!-- `small` (16px) y no `x-small`: a 12px el glifo se perdía junto
                 al texto de la línea 2 y no cumplía su función de identificar
                 el tipo de un vistazo. -->
            <v-icon v-else small class="alert-type-icon">{{ getIncidentIcon(alert) }}</v-icon>
            <span v-if="alertSecondaryLabel(alert)" class="alert-location">{{ alertSecondaryLabel(alert) }}</span>
            <span
              v-if="(alert.type === 'sos' || alert.type === 'sos_call') && alertSosCallType(alert)"
              class="alert-calltype"
            >{{ alertSosCallType(alert) }}</span>
            <span v-if="alert.type !== 'sos' && alert.type !== 'sos_call'" class="alert-time">{{ getAlertTime(alert) }}</span>
          </div>
          <!-- Línea 3 (solo SOS): status de la llamada + tiempo relativo -->
          <div v-if="alert.type === 'sos' || alert.type === 'sos_call'" class="alert-line3">
            <span class="alert-line3-status">{{ alert.subtitle }}</span>
            <span class="alert-time">{{ getAlertTime(alert) }}</span>
          </div>
          <!-- Línea 3 (hits con match de lista negra): es lo que distingue una
               lectura LPR rutinaria de un vehículo con reporte. Sin esto ambas
               se ven idénticas en la lista. -->
          <div v-if="alertBlacklist(alert)" class="alert-line3">
            <span
              class="alert-bl-chip"
              :style="alertBlacklist(alert).color
                ? { color: alertBlacklist(alert).color, borderColor: alertBlacklist(alert).color }
                : null"
              :title="alertBlacklist(alert).nombre"
            >
              <v-icon x-small class="alert-bl-chip__icon">{{ alertBlacklist(alert).icon }}</v-icon>
              <span class="alert-bl-chip__label">{{ alertBlacklist(alert).nombre }}</span>
            </span>
          </div>
        </div>

        <!-- Skeleton durante initial load: evita el flash "Sin alertas en
             esta categoría" en los <1s entre que se monta el componente y
             llegan los primeros batches del WS. Una vez loadingState=null
             el empty state real se muestra solo si efectivamente no hay
             alerts en la categoría. -->
        <template v-if="filteredAlerts.length === 0">
          <div v-if="alertsLoading" class="alert-skeleton-list" aria-busy="true">
            <div v-for="n in 6" :key="'sk-' + n" class="alert-skeleton-item">
              <div class="alert-skeleton-line alert-skeleton-line--title"></div>
              <div class="alert-skeleton-row">
                <div class="alert-skeleton-chip"></div>
                <div class="alert-skeleton-line alert-skeleton-line--meta"></div>
              </div>
            </div>
          </div>
          <div v-else class="monitor-empty">
            <v-icon small color="var(--kln-text-dim)">mdi-bell-off</v-icon>
            <span>Sin alertas en esta categoría</span>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { ENTITY_CONFIGS, hasConnectionStatus } from '../data/entity-configs.js'
import { resolveDeviceType, deviceTypeLabel } from '../data/device-type-resolver.js'
import { timeAgo } from '../utils/map-constants.js'
import { resolveIncidentGroup, getIncidentMdiIcon, getIncidentCategoryLabel, getIncidentColorVar, INCIDENT_MDI_BY_CATEGORY } from '../data/incident-category-resolver.js'
import { detectPartnerSystem, getPartnerMeta } from '../data/partner-system.js'
import {
  ensureDriverDirectory, isDriverDirectoryLoaded, getDriverHaystackForRaw, normalizeText
} from '../data/driver-directory.js'
import { hitIcon } from '../data/hit-icons.js'
import { parsePhone } from '@/util/phone-format'
import { capaPermitida, categoriaVisible } from '../data/layer-permissions.js'

const MAX_ITEMS = 200

export default {
  name: 'MapSidebar',

  props: {
    monitors: {
      type: Object,
      default: function() { return {} },
    },
    visibleMonitorIds: {
      type: Object,
      default: function() { return {} },
    },
    alerts: {
      type: Array,
      default: () => [],
    },
    // Conteos por categoría calculados imperativamente en MapV2.vue
    // (_alertCounts). Reemplaza el loop O(N) sobre `alerts` que tenía
    // este componente cada vez que el array mutaba.
    alertCounts: {
      type: Object,
      default: function() {
        return { '911': 0, 'sos': 0, 'event': 0, 'hit': 0, 'reportant': 0 }
      },
    },
    selectedMonitorId: {
      type: String,
      default: null,
    },
    tick: {
      type: Number,
      default: 0,
    },
    // True mientras MapV2 está en su initial load (loadingState != null).
    // Sustituye el empty state "Sin alertas" por un skeleton durante ese
    // período breve donde alerts=[] todavia no refleja el estado real.
    alertsLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeTab: parseInt(localStorage.getItem('kalan:map-v2:sidebarTab') || '1'),
      selectedEntity: 'device',
      search: '',
      // Bump cuando el directorio lazy de elementos termina de cargar —
      // filteredMonitors lo referencia para re-filtrar sin re-teclear.
      driverDirTick: 0,
      // Una búsqueda de texto independiente por cada categoría de alertas
      // para que cambiar de 911 → SOS → Reportantes no herede ni pise el
      // término del tab anterior.
      alertSearchByCategory: {
        '911': '',
        'sos': '',
        'event': '',
        'hit': '',
        'reportant': '',
      },
      alertCategory: '911',
      alertFilterPriority: null,
      alertFilterLocation: null,
      alertFilterCategory: null,
      alertFilterMenu: false,
      alertSortBy: 'time-desc',  // 'time-desc' | 'time-asc' | 'folio-desc' | 'folio-asc'
      // Ordenamiento por prioridad (combina con alertSortBy). 3 estados:
      //   null  → sin ordenar por prioridad (solo time/folio).
      //   'desc' → ALTA primero, luego MEDIA, luego BAJA, luego sin prioridad.
      //   'asc'  → BAJA primero, luego MEDIA, luego ALTA, luego sin prioridad.
      // Dentro del mismo nivel de prioridad, el tiebreaker es alertSortBy.
      alertPriorityDir: null,
      sortDesc: false,
      statusFilter: null, // null, 'online', 'offline'
      // Filtro por tipo operativo del device (Ambulancia, Patrulla
      // Municipal, Tránsito, etc.). Solo aplica cuando selectedEntity ===
      // 'device'. Null = ver todos. Resetea al cambiar de entity tab.
      deviceTypeFilter: null,
      viewportOnly: false,
      entityConfigs: ENTITY_CONFIGS,
      maxItems: MAX_ITEMS,
      // Colores elegidos para ser VISUALMENTE DISTINTOS entre sí (cyan /
      // rojo brillante / amber / violeta / verde). Se replican idénticos
      // en MapToolbar.vue (alertChips) para mantener la UX consistente.
      alertCategories: [
        { key: '911', label: '9-1-1', icon: 'mdi-pulse', color: 'var(--kln-accent)' },
        { key: 'sos', label: 'SOS', icon: 'mdi-phone-in-talk', color: 'var(--kln-alert-danger)' },
        { key: 'event', label: 'Alertas', icon: 'mdi-alarm-light', color: 'var(--kln-alert-warning)' },
        { key: 'hit', label: 'Hits', icon: 'mdi-camera-front', color: 'var(--kln-entity-violet)' },
        { key: 'reportant', label: 'Reportantes', icon: 'mdi-account-alert', color: 'var(--kln-entity-green)' },
      ],
      // Estado de scroll horizontal del tab container de alertas.
      alertScroll: { canLeft: false, canRight: false },
      entityScroll: { canLeft: false, canRight: false },
      // IDs de los tipos de entidad visibles inline en el tab bar de
      // Monitoreo. Se reconcilia con entityConfigs en el watcher. Los
      // primeros 3 tipos (device/camera/bus) son los principales; el resto
      // vive en el dropdown "+N".
      visibleEntityIds: [],
      entityOverflowMenu: false,
    }
  },

  mounted() {
    // Inicializar estado de scroll y observers para los dos tab containers.
    this._scrollObservers = []
    this.$nextTick(() => {
      this._setupScrollObserver('alertScrollEl', 'alertScroll')
      this._setupScrollObserver('entityScrollEl', 'entityScroll')
    })
    // Window resize: recomputar ambos estados cuando el sidebar cambia
    // de tamano (ej. drawer abierto/cerrado, ventana resize)
    this._onWindowResizeForTabs = () => {
      this._updateTabScrollState('alertScrollEl', 'alertScroll')
      this._updateTabScrollState('entityScrollEl', 'entityScroll')
    }
    window.addEventListener('resize', this._onWindowResizeForTabs)
  },

  beforeDestroy() {
    // Limpiar ResizeObservers para evitar memory leaks
    if (this._scrollObservers) {
      this._scrollObservers.forEach(function(obs) { obs.disconnect() })
      this._scrollObservers = null
    }
    if (this._onWindowResizeForTabs) {
      window.removeEventListener('resize', this._onWindowResizeForTabs)
    }
  },

  watch: {
    activeTab: function(val) {
      localStorage.setItem('kalan:map-v2:sidebarTab', val)
      // Re-inicializar el observer del tab que aparece (porque el otro
      // estaba oculto con v-if y su ref no existia hasta ahora)
      this.$nextTick(() => {
        if (val === 0) this._setupScrollObserver('alertScrollEl', 'alertScroll')
        else if (val === 1) this._setupScrollObserver('entityScrollEl', 'entityScroll')
      })
    },

    // Resetea el filtro de tipo cuando el operador cambia de pestana de
    // entidad — solo tiene sentido para device, y persistirlo confundiria
    // si vuelve a un tab donde no aplica.
    selectedEntity: function(val) {
      this.deviceTypeFilter = null
      // Si la entidad nueva no tiene estado de conexion, el filtro se limpia:
      // el control desaparece del header, y dejarlo activo escondido dejaba la
      // lista vacia sin nada visible que explicara por que (elegir Incidentes
      // con "en linea" puesto mostraba cero incidentes y ningun filtro a la
      // vista para quitarlo).
      if (val && !hasConnectionStatus(val)) this.statusFilter = null
    },

    // Carga lazy del directorio de elementos al primer uso de la búsqueda
    // de Monitoreo. Al resolver, driverDirTick re-dispara filteredMonitors
    // para que los matches por nombre de persona aparezcan sin re-teclear.
    search: function(val) {
      if (!val || !val.trim() || isDriverDirectoryLoaded()) return
      var self = this
      ensureDriverDirectory().then(function() {
        self.driverDirTick++
      })
    },

    // Reconcilia visibleEntityIds con entityConfigs: mantiene los slots
    // que sigan existiendo, rellena hasta maxVisibleEntities con los
    // primeros tipos que aun no esten dentro. Tambien promueve el
    // selectedEntity al slot 0 si quedo fuera (ej. al seleccionar desde
    // el dropdown "+N"). Comportamiento analogo al de MapDrawerTabs.
    allEntityTypes: {
      immediate: true,
      handler: function onEntityTypesChange(types) {
        var self = this
        // La pestaña por defecto ('device') puede no estar permitida por el
        // perfil: cae a la primera capa permitida.
        if (self.selectedEntity && types.indexOf(self.selectedEntity) === -1) {
          self.selectedEntity = types.length ? types[0] : null
        }
        var tabIdsSet = {}
        types.forEach(function(id) { tabIdsSet[id] = true })
        var kept = self.visibleEntityIds.filter(function(id) { return tabIdsSet[id] })
        var keptSet = {}
        kept.forEach(function(id) { keptSet[id] = true })
        for (var j = 0; j < types.length && kept.length < self.maxVisibleEntities; j++) {
          if (!keptSet[types[j]]) {
            kept.push(types[j])
            keptSet[types[j]] = true
          }
        }
        if (self.selectedEntity && tabIdsSet[self.selectedEntity] && !keptSet[self.selectedEntity]) {
          if (kept.length >= self.maxVisibleEntities) kept[0] = self.selectedEntity
          else kept.unshift(self.selectedEntity)
        }
        self.visibleEntityIds = kept
      },
    },
  },

  computed: {
    maxVisibleEntities() { return 3 },

    // Capas permitidas por el perfil (Parte D), del getter accesos/capasPermitidas.
    capas() {
      return this.$store.getters['accesos/capasPermitidas']
    },

    allEntityTypes() {
      var capas = this.capas
      return Object.keys(this.entityConfigs).filter(function(t) { return capaPermitida(capas, t) })
    },

    visibleEntityTabs() {
      var cfgs = this.entityConfigs
      return this.visibleEntityIds.reduce(function(acc, id) {
        if (cfgs[id]) acc.push({ type: id, cfg: cfgs[id] })
        return acc
      }, [])
    },

    overflowEntityTabs() {
      var visible = {}
      this.visibleEntityIds.forEach(function(id) { visible[id] = true })
      var cfgs = this.entityConfigs
      return this.allEntityTypes.reduce(function(acc, id) {
        if (!visible[id] && cfgs[id]) acc.push({ type: id, cfg: cfgs[id] })
        return acc
      }, [])
    },

    monitorsArray() {
      // Depend on tick to force re-evaluation when non-reactive monitors change
      void this.tick
      // Only compute when on Monitoreo tab — avoid 19K Object.values when on Alertas
      if (this.activeTab !== 1) return []
      return Object.values(this.monitors)
    },

    // Pre-computed counts — respects viewportOnly toggle
    viewportCounts() {
      void this.tick
      var counts = {}
      var useViewport = this.viewportOnly && Object.keys(this.visibleMonitorIds).length > 0
      if (useViewport) {
        var visibleKeys = Object.keys(this.visibleMonitorIds)
        for (var i = 0; i < visibleKeys.length; i++) {
          var m = this.monitors[visibleKeys[i]]
          if (m) counts[m.entityType] = (counts[m.entityType] || 0) + 1
        }
      } else {
        var all = this.monitorsArray
        for (var j = 0; j < all.length; j++) {
          counts[all[j].entityType] = (counts[all[j].entityType] || 0) + 1
        }
      }
      return counts
    },

    // Categorias que este operador puede ver. Reportantes (RAPID-SOS) expone
    // el telefono de quien llamo al 911: sin permiso la pestana no se dibuja,
    // igual que en el legacy (map/rightPanel/MapAlerts.vue:31). El split en
    // filas parte de esta lista, no de alertCategories, para que al caer a 4
    // categorias queden 2 arriba + 2 abajo en vez de dejar un hueco.
    visibleAlertCategories() {
      var capas = this.capas
      return this.alertCategories.filter(function(c) { return categoriaVisible(c.key, capas) })
    },

    // Split en 2 filas para el grid 2-row. Row1 toma los primeros ceil(N/2),
    // row2 el resto. Con 5 categorias queda 3 arriba + 2 abajo; cada fila se
    // reparte su ancho uniforme.
    alertCategoriesRow1() {
      var half = Math.ceil(this.visibleAlertCategories.length / 2)
      return this.visibleAlertCategories.slice(0, half)
    },

    alertCategoriesRow2() {
      var half = Math.ceil(this.visibleAlertCategories.length / 2)
      return this.visibleAlertCategories.slice(half)
    },

    // Conteos pre-calculados por MapV2 (prop). Antes este computed recorría
    // this.alerts O(N) en cada mutación del array — era una de las funciones
    // más calientes del trace según DevTools.
    alertCategoryCounts() {
      // Conteo por categoría desde el prop (calculado en MapV2). Los
      // reportantes ya vienen filtrados a < 20 min en la fuente (sweep
      // _cleanupStaleReportants / _reportantIsStale), así que el badge, el
      // listado y el contador del toolbar coinciden sin lógica extra acá.
      return this.alertCounts || { '911': 0, 'sos': 0, 'event': 0, 'hit': 0, 'reportant': 0 }
    },

    // ¿La entidad seleccionada tiene estado de conexion? Gobierna el filtro
    // "en linea / fuera de linea", los dos contadores y la barra proporcional.
    // Sin entidad seleccionada la lista mezcla tipos y el filtro sigue siendo
    // util para los que si lo tienen, asi que ahi se muestra.
    showConnectionStatus() {
      if (!this.selectedEntity) return true
      return hasConnectionStatus(this.selectedEntity)
    },

    filterOptions() {
      return [
        { value: 'all', label: 'Todos', icon: 'mdi-filter-variant', color: '' },
        { value: 'online', label: 'En línea', icon: 'mdi-circle', color: 'var(--kln-status-online)' },
        { value: 'offline', label: 'Fuera de línea', icon: 'mdi-circle-outline', color: 'var(--kln-status-offline)' },
      ]
    },

    currentFilter() {
      if (this.statusFilter === 'online') return 'online'
      if (this.statusFilter === 'offline') return 'offline'
      return 'all'
    },

    activeFilterLabel() {
      var opt = this.filterOptions.find(function(o) { return o.value === this.currentFilter }.bind(this))
      return opt ? opt.label : 'Todos'
    },

    activeFilterColor() {
      var opt = this.filterOptions.find(function(o) { return o.value === this.currentFilter }.bind(this))
      return opt && opt.color ? opt.color : 'var(--kln-text-dim)'
    },

    activeFilterClass() {
      if (this.currentFilter === 'all') return ''
      return 'filter-dropdown--active'
    },

    // Opciones del filtro de tipo: solo los slugs presentes en los devices
    // actualmente cargados, con conteo. Se ordena alfabeticamente por
    // label, salvo "Sin clasificar" que va al final. La opcion "Todos"
    // (deviceTypeFilter=null) la renderiza el template aparte.
    deviceTypeFilterOptions() {
      if (this.selectedEntity !== 'device') return []
      var counts = {}
      var list = this.monitorsArray
      for (var i = 0; i < list.length; i++) {
        if (list[i].entityType !== 'device') continue
        var slug = resolveDeviceType(list[i].data || {})
        counts[slug] = (counts[slug] || 0) + 1
      }
      var slugs = Object.keys(counts)
      var opts = slugs.map(function(slug) {
        return { slug: slug, label: deviceTypeLabel(slug), count: counts[slug] }
      })
      opts.sort(function(a, b) {
        if (a.slug === 'otros' && b.slug !== 'otros') return 1
        if (b.slug === 'otros' && a.slug !== 'otros') return -1
        return a.label.localeCompare(b.label)
      })
      return opts
    },

    deviceTypeFilterLabel() {
      if (!this.deviceTypeFilter) return 'Todos los tipos'
      return deviceTypeLabel(this.deviceTypeFilter)
    },

    entityStatusCounts() {
      if (!this.selectedEntity) return { online: 0, offline: 0, total: 0, onlinePct: 0, offlinePct: 0 }
      var online = 0, offline = 0
      var useViewport = this.viewportOnly && Object.keys(this.visibleMonitorIds).length > 0
      var list = this.monitorsArray
      for (var i = 0; i < list.length; i++) {
        if (list[i].entityType !== this.selectedEntity) continue
        if (useViewport && !this.visibleMonitorIds[list[i].id]) continue
        if (list[i].status === 'online') online++
        else offline++
      }
      var total = online + offline
      return {
        online: online,
        offline: offline,
        total: total,
        onlinePct: total > 0 ? Math.round(online / total * 100) : 0,
        offlinePct: total > 0 ? Math.round(offline / total * 100) : 0,
      }
    },

    filteredMonitors() {
      let list = this.monitorsArray

      // Filter by viewport (only when toggle is active)
      if (this.viewportOnly && Object.keys(this.visibleMonitorIds).length > 0) {
        list = list.filter(m => !!this.visibleMonitorIds[m.id])
      }

      // Filter by selected entity type
      if (this.selectedEntity) {
        list = list.filter(m => m.entityType === this.selectedEntity)
      }

      // Filter by status
      if (this.statusFilter) {
        list = list.filter(m => m.status === this.statusFilter)
      }

      // Filter por tipo operativo (solo aplica al tab de devices). El slug
      // se resuelve on-the-fly desde el raw data para usar la misma logica
      // que pinta el icono — asi el filtro coincide siempre con lo visible.
      if (this.selectedEntity === 'device' && this.deviceTypeFilter) {
        list = list.filter(m => resolveDeviceType(m.data || {}) === this.deviceTypeFilter)
      }

      // Filter by search text. Normalizado sin acentos, y para devices
      // también matchea el nombre del elemento (conductor) — requerimiento
      // "Buscar por nombre en los elementos/dispositivo". driverDirTick
      // crea la dependencia reactiva: cuando el directorio lazy termina de
      // cargar, la lista se re-filtra sola.
      if (this.search && this.search.trim()) {
        void this.driverDirTick
        const q = normalizeText(this.search.trim())
        list = list.filter(m =>
          (m.title && normalizeText(m.title).includes(q)) ||
          (m.label && normalizeText(m.label).includes(q)) ||
          (m.subtitle && normalizeText(m.subtitle).includes(q)) ||
          (m.entityType === 'device' && getDriverHaystackForRaw(m.data).includes(q))
        )
      }

      // Sort
      list = list.slice().sort((a, b) => {
        const aTitle = (a.title || '').toLowerCase()
        const bTitle = (b.title || '').toLowerCase()
        return this.sortDesc ? bTitle.localeCompare(aTitle) : aTitle.localeCompare(bTitle)
      })

      // Cap at max — don't spread/clone, use original references
      return list.slice(0, MAX_ITEMS)
    },

    // Proxy de v-model → alertSearchByCategory[categoriaActiva]. Cambiar
    // de categoría hace que el v-text-field lea otra entrada del objeto.
    alertSearch: {
      get() {
        return this.alertSearchByCategory[this.alertCategory] || ''
      },
      set(value) {
        // v-text-field clearable emite null cuando el usuario presiona la X
        this.$set(this.alertSearchByCategory, this.alertCategory, value || '')
      },
    },

    hasActiveFilters() {
      return !!this.alertFilterPriority || !!this.alertFilterLocation || !!this.alertFilterCategory
    },

    priorityOptions() {
      return ['ALTA', 'MEDIA', 'BAJA']
    },

    // Categorías CNIE v3.0 que el operador puede filtrar en el tab 911.
    // Cada option { text, value, icon } donde value es el slug que
    // devuelve resolveIncidentGroup() e icon viene del mapping centralizado
    // INCIDENT_MDI_BY_CATEGORY — misma fuente que el header del drawer y
    // los chips de cada item del listado.
    categoryOptions() {
      return [
        { text: 'Seguridad', value: 'seguridad', icon: INCIDENT_MDI_BY_CATEGORY.seguridad },
        { text: 'Médicos', value: 'medico', icon: INCIDENT_MDI_BY_CATEGORY.medico },
        { text: 'Protección Civil', value: 'proteccion_civil', icon: INCIDENT_MDI_BY_CATEGORY.proteccion_civil },
        { text: 'Servicios Públicos', value: 'servicios_publicos', icon: INCIDENT_MDI_BY_CATEGORY.servicios_publicos },
        { text: 'Asistencia', value: 'asistencia', icon: INCIDENT_MDI_BY_CATEGORY.asistencia },
        { text: 'Otros Servicios', value: 'otros_servicios', icon: INCIDENT_MDI_BY_CATEGORY.otros_servicios },
        { text: 'Mal uso del servicio', value: 'mal_uso', icon: INCIDENT_MDI_BY_CATEGORY.mal_uso },
      ]
    },

    locationOptions() {
      // Only compute when filter popup is open (alertFilterMenu)
      if (!this.alertFilterMenu) return []
      var locs = {}
      for (var i = 0; i < this.alerts.length; i++) {
        var addr = this.alerts[i].data && this.alerts[i].data.address
        if (!addr) continue
        var s = String(addr).trim()
        // Evitar "N/A" y strings vacios como opciones de filtro (el backend
        // los manda como fallback cuando no hay direccion conocida).
        if (s === '' || s.toUpperCase() === 'N/A' || s === '—') continue
        locs[addr] = true
      }
      return Object.keys(locs).sort()
    },

    filteredAlerts() {
      var categoryMap = {
        '911': ['incident'],
        'sos': ['sos', 'sos_call'],
        'event': ['event', 'bus_alert', 'device_alert', 'sensor_alert', 'external_sensor_alert'],
        'hit': ['hit'],
        'reportant': ['reportant'],
      }
      var validTypes = categoryMap[this.alertCategory] || []
      var self = this
      // Los filtros de prioridad y localidad solo tienen sentido en 911;
      // las otras categorías no cargan esos campos y los compartiríamos
      // a ciegas si los aplicáramos siempre. Los reportantes > 20 min ya se
      // evictan en la fuente (MapV2 _cleanupStaleReportants), así que no hay
      // que filtrarlos acá — el listado y el contador del toolbar coinciden.
      var applyIncidentFilters = this.alertCategory === '911'
      var list = this.alerts.filter(function(a) {
        if (validTypes.indexOf(a.type) === -1) return false
        if (!applyIncidentFilters) return true
        if (self.alertFilterPriority && a.data && (a.data.priority || '').toUpperCase() !== self.alertFilterPriority) return false
        if (self.alertFilterLocation && (!a.data || a.data.address !== self.alertFilterLocation)) return false
        if (self.alertFilterCategory) {
          var slug = resolveIncidentGroup(a.data || {})
          if (slug !== self.alertFilterCategory) return false
        }
        return true
      })

      // Text search
      if (this.alertSearch && this.alertSearch.trim()) {
        var q = this.alertSearch.trim().toLowerCase()
        list = list.filter(function(a) {
          return (a.title && a.title.toLowerCase().indexOf(q) !== -1) ||
                 (a.subtitle && a.subtitle.toLowerCase().indexOf(q) !== -1) ||
                 (a.data && a.data.address && a.data.address.toLowerCase().indexOf(q) !== -1)
        })
      }

      // Sort — puede combinar prioridad (primary) + time/folio (tiebreaker).
      // Orden de prioridad: ALTA=3, MEDIA=2, BAJA=1, sin prioridad=0.
      var priorityRank = function(alert) {
        var p = alert && alert.data && alert.data.priority
        if (!p) return 0
        var s = String(p).trim().toUpperCase()
        if (s === 'ALTA' || s === 'HIGH') return 3
        if (s === 'MEDIA' || s === 'MEDIUM') return 2
        if (s === 'BAJA' || s === 'LOW') return 1
        return 0
      }
      list = list.slice().sort(function(a, b) {
        if (self.alertPriorityDir) {
          var pa = priorityRank(a)
          var pb = priorityRank(b)
          if (pa !== pb) {
            return self.alertPriorityDir === 'desc' ? pb - pa : pa - pb
          }
        }
        // Tiebreaker dentro del mismo nivel de prioridad (o siempre si no
        // hay sort por prioridad activo).
        var sortBy = self.alertSortBy
        if (sortBy === 'folio-asc') {
          return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true })
        }
        if (sortBy === 'folio-desc') {
          return (b.title || '').localeCompare(a.title || '', undefined, { numeric: true })
        }
        var ta = new Date(a.createdAt || 0).getTime()
        var tb = new Date(b.createdAt || 0).getTime()
        if (sortBy === 'time-asc') return ta - tb       // más viejo primero
        return tb - ta                                   // time-desc: más reciente primero (default)
      })

      return list.slice(0, MAX_ITEMS)
    },

    alertSortByIcon() {
      switch (this.alertSortBy) {
        case 'time-asc':   return 'mdi-sort-clock-ascending-outline'
        case 'folio-desc': return 'mdi-sort-numeric-descending'
        case 'folio-asc':  return 'mdi-sort-numeric-ascending'
        default:           return 'mdi-sort-clock-descending-outline'  // time-desc
      }
    },

    alertSortByTooltip() {
      switch (this.alertSortBy) {
        case 'time-desc':  return 'Tiempo: más reciente primero (clic → más viejo primero)'
        case 'time-asc':   return 'Tiempo: más viejo primero (clic → folio descendente)'
        case 'folio-desc': return 'Folio: Z→A (clic → A→Z)'
        case 'folio-asc':  return 'Folio: A→Z (clic → tiempo)'
        default:           return 'Ordenar'
      }
    },

    priorityDirIcon() {
      if (this.alertPriorityDir === 'desc') return 'mdi-sort-variant'
      if (this.alertPriorityDir === 'asc') return 'mdi-sort-reverse-variant'
      return 'mdi-flag-outline'
    },

    priorityDirTooltip() {
      if (this.alertPriorityDir === 'desc') return 'Prioridad: ALTA primero (clic → BAJA primero)'
      if (this.alertPriorityDir === 'asc') return 'Prioridad: BAJA primero (clic → desactivar)'
      return 'Ordenar por prioridad'
    },

    // Derived from filteredMonitors — no duplicate filtering
    totalCount() {
      return this.monitorsArray.length
    },

    // Hay overflow horizontal en alguna direccion → mostrar AMBAS
    // flechas (la del extremo opuesto sirve solo como indicador y
    // queda visualmente disabled). Asi las flechas nunca desaparecen
    // bajo el cursor del usuario mientras navega.
    alertHasOverflow() {
      return this.alertScroll.canLeft || this.alertScroll.canRight
    },
    entityHasOverflow() {
      return this.entityScroll.canLeft || this.entityScroll.canRight
    },
  },

  methods: {
    // ─── Scroll horizontal de tabs (alert/entity) ──────────────

    _setupScrollObserver(refName, stateKey) {
      const el = this.$refs[refName]
      if (!el) return
      const update = () => this._updateTabScrollState(refName, stateKey)
      // Listener de scroll: actualiza canLeft/canRight cuando el usuario
      // scrollea con trackpad/touch
      el.addEventListener('scroll', update, { passive: true })
      // ResizeObserver: detecta cambios de ancho del container o de
      // CUALQUIERA de sus hijos (cada chip individualmente). Asi
      // capturamos el caso donde los chips renderizan despues del
      // mount inicial y cambian el scrollWidth.
      if (typeof ResizeObserver !== 'undefined') {
        const obs = new ResizeObserver(update)
        obs.observe(el)
        // Observar TODOS los hijos para capturar cambios de tamano
        Array.prototype.forEach.call(el.children, function(child) {
          obs.observe(child)
        })
        this._scrollObservers.push(obs)
      }
      // Initial state — multiples intentos por si el layout no
      // ha terminado de calcularse en el primer frame:
      update()                                              // sync
      requestAnimationFrame(update)                          // next paint
      requestAnimationFrame(() => requestAnimationFrame(update)) // 2 frames
      setTimeout(update, 50)                                 // safety net
      setTimeout(update, 200)                                // last resort
    },

    _updateTabScrollState(refName, stateKey) {
      const el = this.$refs[refName]
      if (!el) {
        this[stateKey] = { canLeft: false, canRight: false }
        return
      }
      // -1 de tolerancia por subpixel rounding
      const canLeft = el.scrollLeft > 0
      const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      // Solo asignar si cambio para no triggear re-renders innecesarios
      if (this[stateKey].canLeft !== canLeft || this[stateKey].canRight !== canRight) {
        this[stateKey] = { canLeft, canRight }
      }
    },

    scrollTabsBy(refName, delta) {
      const el = this.$refs[refName]
      if (!el) return
      el.scrollBy({ left: delta, behavior: 'smooth' })
    },

    clearAlertFilters() {
      this.alertFilterPriority = null
      this.alertFilterLocation = null
      this.alertFilterCategory = null
    },

    // Detecta si un alert es un incidente 911 con categoría CNIE. Los demás
    // tipos (sos, hit, sensor_alert, bus_alert, etc.) tienen su propio ícono
    // y NO se renderean con el chip de categoría coloreado.
    isCnieIncident(alert) {
      if (!alert || !alert.data) return false
      return alert.type === 'incident' && !!alert.data.priority
    },

    // Helpers expuestos al template para que el chip de categoría lea
    // del módulo unificado (mismo origen que el header del drawer y los
    // SVGs pre-pintados del marker).
    getIncidentMdiIcon: getIncidentMdiIcon,
    getIncidentCategoryLabel: getIncidentCategoryLabel,
    getIncidentColorVar: getIncidentColorVar,

    getIncidentIcon(alert) {
      if (!alert.data) return 'mdi-alert-circle'
      if (alert.type === 'reportant') return 'mdi-account-alert'
      if (alert.type === 'sos' || alert.type === 'sos_call') return 'mdi-phone-alert'
      // El tab "Hits" mezcla LPR, mandamientos, robo de vehículo y video
      // analítico; el ícono distingue el subtipo (ver hit-icons.js).
      if (alert.type === 'hit') return hitIcon(alert.data.typeOfItem, alert.data.hitType)
      // Alertas de panico: diferenciar icono segun el tipo de entidad que
      // lo disparo. Antes todo caia en mdi-alarm-light y no se distinguian
      // buses de sensores fisicos en la lista. Icons alineados con
      // ENTITY_CONFIGS[*].mdiIcon para consistencia visual con el mapa.
      if (alert.type === 'bus_alert') return 'mdi-bus'
      if (alert.type === 'device_alert') return 'mdi-car-emergency'
      if (alert.type === 'external_sensor_alert') return 'mdi-access-point-network'
      if (alert.type === 'sensor_alert') return 'mdi-alarm-light'  // boton de panico fisico
      if (alert.type === 'event') return 'mdi-bell-ring'
      // Incidentes 911: misma fuente de verdad que el header del drawer y el
      // marker. Antes era un set de indexOf case-sensitive sobre el string
      // raw de category que solo cubria 4/7 grupos y caia a un fallback
      // distinto al del marker, generando inconsistencia visual.
      return getIncidentMdiIcon(alert.data)
    },

    // Cicla el criterio + dirección del sort principal del listado de
    // alertas: time-desc → time-asc → folio-desc → folio-asc → (loop).
    // Combina con alertPriorityDir cuando este está activo.
    cycleAlertSortBy() {
      var seq = ['time-desc', 'time-asc', 'folio-desc', 'folio-asc']
      var i = seq.indexOf(this.alertSortBy)
      this.alertSortBy = seq[(i + 1) % seq.length]
    },

    // Avanza el toggle de prioridad en 3 estados: inactivo → ALTA arriba
    // → BAJA arriba → inactivo. Combina con alertSortBy como tiebreaker.
    cyclePriorityDir() {
      if (this.alertPriorityDir === null) this.alertPriorityDir = 'desc'
      else if (this.alertPriorityDir === 'desc') this.alertPriorityDir = 'asc'
      else this.alertPriorityDir = null
    },

    // Devuelve la clase modificadora del .alert-item según la prioridad
    // normalizada del incidente. La clase pinta una barra vertical a la
    // izquierda (border-left) con el color de prioridad — feedback del
    // operador: ALTA=rojo, MEDIA=amarillo/naranja, BAJA=verde. Resto de
    // alerts (SOS, hits, reportants, etc.) sin prioridad → sin barra.
    alertPriorityClass(alert) {
      var raw = alert && alert.data && alert.data.priority
      if (!raw) return ''
      var s = String(raw).trim().toUpperCase()
      if (s === 'ALTA' || s === 'HIGH') return 'alert-item--priority-alta'
      if (s === 'MEDIA' || s === 'MEDIUM') return 'alert-item--priority-media'
      if (s === 'BAJA' || s === 'LOW') return 'alert-item--priority-baja'
      return ''
    },

    setFilter: function(value) {
      if (value === 'all') {
        this.statusFilter = null
      } else {
        this.statusFilter = value
      }
    },

    // LPR: el identificador (MXBUS004) va antes del status bajo el nombre.
    getSubtitleLine(monitor) {
      var d = monitor.data
      if (monitor.entityType === 'lpr' && d && d.identifier) {
        return monitor.subtitle ? d.identifier + ' · ' + monitor.subtitle : d.identifier
      }
      return monitor.subtitle
    },

    getDetailLine(monitor) {
      var d = monitor.data
      if (!d) return null
      var type = monitor.entityType
      // Camera / LPR: address
      if (type === 'camera' || type === 'lpr') return d.address || d.location || null
      // Device: group + category
      if (type === 'device') return d.groupName || null
      // Bus: address or group
      if (type === 'bus') return d.address || d.groupName || null
      // Incident: address
      if (type === 'incident') {
        var attrs = d.attributes || {}
        return attrs.direccion || attrs.address || d.address || null
      }
      // Sensor: type/category
      if (type === 'sensor') return d.category && d.category.name ? d.category.name : null
      return null
    },

    selectEntity(type) {
      this.selectedEntity = this.selectedEntity === type ? null : type
    },

    // Click en una entry del dropdown "+N": selecciona ese tipo y promueve
    // su chip al slot 0 visible (patron analogo a MapDrawerTabs). Asi el
    // operador puede alternar entre los 3 visibles sin re-abrir el menu.
    onEntityOverflowSelect(type) {
      this.entityOverflowMenu = false
      if (!this.visibleEntityIds.includes(type)) {
        var updated = this.visibleEntityIds.slice()
        if (updated.length >= this.maxVisibleEntities) updated[0] = type
        else updated.unshift(type)
        this.visibleEntityIds = updated
      }
      this.selectedEntity = type
    },

    getEntityTabStyle(type) {
      if (this.selectedEntity === type) {
        var cfg = ENTITY_CONFIGS[type]
        return {
          background: cfg.color + '22',
        }
      }
      return {}
    },

    getViewportCount(type) {
      var count = 0
      var visibleKeys = Object.keys(this.visibleMonitorIds)

      // If viewport tracking is active, count only visible monitors
      // Otherwise, count all monitors of this type
      if (visibleKeys.length > 0) {
        for (var i = 0; i < visibleKeys.length; i++) {
          var m = this.monitors[visibleKeys[i]]
          if (m && m.entityType === type) count++
        }
      } else {
        var allMonitors = Object.values(this.monitors)
        for (var j = 0; j < allMonitors.length; j++) {
          if (allMonitors[j].entityType === type) count++
        }
      }
      return count
    },

    getEntityIcon(entityType) {
      return ENTITY_CONFIGS[entityType]?.mdiIcon || 'mdi-map-marker'
    },

    getRelativeTime(monitor) {
      if (!monitor.data) return ''
      const d = monitor.data
      return timeAgo(d.updatedAt || d.serverTime || d.createdAt || d.created_at)
    },

    getAlertColor(alert) {
      if (alert.entityType === 'sos_call') return 'var(--kln-alert-danger)'
      if (alert.priority === 'alta' || alert.priority === 'high') return 'var(--kln-alert-danger)'
      if (alert.priority === 'media' || alert.priority === 'medium') return 'var(--kln-alert-warning)'
      return 'var(--kln-accent)'
    },

    getAlertIcon(alert) {
      if (alert.entityType === 'sos_call') return 'mdi-phone-alert'
      if (alert.type === 'incident') return 'mdi-alert-circle'
      return 'mdi-bell-ring'
    },

    // Logo del proveedor de interconexión externa (Carbyne/DiDi/Uber/
    // RapidSOS) si la alerta es un reportant o un incidente con
    // rapidSosLocations adjunto. Devuelve `{logo, label}` o null.
    //
    // Pasamos `alert.type` como entityType al detector para que un
    // reportant defaultee a 'rapidsos' aunque su additionalData no
    // traiga systemName (caso común — el WS legacy a veces emite el
    // payload sin esa key). Para incident solo retorna si hay señal
    // explícita (rapidSosLocations / systemName) — un incident sin
    // enriquecer NO pinta badge.
    alertPartner(alert) {
      if (!alert || !alert.data) return null
      if (alert.type !== 'reportant' && alert.type !== 'incident') return null
      var id = detectPartnerSystem(alert.data, alert.type)
      return id ? getPartnerMeta(id) : null
    },

    getAlertTime(alert) {
      return timeAgo(alert.createdAt || alert.serverTime || alert.created_at)
    },

    // Matchea la fila de alert con el selectedMonitorId del drawer para
    // destacar cual esta abierto actualmente. Los alerts tienen 2 formatos
    // de id: los directos (incident-, sos_call-, reportant-) coinciden 1:1
    // con monitorId; los panicos (bus-alert-, sensor-alert-, etc) usan un
    // sufijo "-alert-" distinto. Para esos, reconstruimos el monitorId
    // esperado igual que lo hace onAlertClick en MapV2.vue (entityType-
    // dataId). Tambien cubrimos el flujo _openAlertDrawer que setea
    // monitorId directamente al alert.id.
    isAlertSelected(alert) {
      var sel = this.selectedMonitorId
      if (!sel || !alert) return false
      if (alert.id === sel) return true
      var dataId = alert.data && (alert.data.id || alert.data._id || alert.data.callId)
      if (!dataId) return false
      var mapped = (alert.entityType || 'event') + '-' + dataId
      return mapped === sel
    },

    // Etiqueta primaria del alert row. Para alertas de panico (bus, sensor,
    // device, external_sensor) preferimos el title (nombre de la unidad o
    // sensor) porque el subtitle es siempre el generico "Boton de Panico
    // Activado" y todas las filas quedaban iguales. Para reportantes el
    // title es el TELEFONO (dato mas importante segun el cliente — paridad
    // con el listado de reportantes del legacy). Para incidentes, hits y
    // SOS el subtitle suele traer el detalle especifico (tipo de incidente,
    // folio, etc.) asi que se preserva el comportamiento previo. Si no hay
    // nada, cae al tipo del alert como ultimo recurso.
    alertPrimaryLabel(alert) {
      // 'sos'/'sos_call': el title es el nombre del reportante (userData), que
      // es el dato más relevante — no el status (que va de subtitle). Antes
      // SOS caía al branch de abajo y mostraba "Finalizada por cliente" como
      // título en vez del nombre.
      var titleFirstTypes = ['sensor_alert', 'external_sensor_alert', 'bus_alert', 'device_alert', 'reportant', 'sos', 'sos_call']
      if (titleFirstTypes.indexOf(alert.type) !== -1) {
        return alert.title || alert.subtitle || alert.type || ''
      }
      return alert.subtitle || alert.title || alert.type || ''
    },

    // Etiqueta secundaria (linea 2) del alert row. Para incidentes y
    // reportantes mostramos direccion (es lo mas util). Para buses
    // priorizamos grupo/ruta ya que el backend casi siempre manda
    // address === "N/A" y el grupo identifica la ruta de transporte.
    // Para devices (patrullas) mostramos el grupo (corporacion/sector).
    // "N/A" o vacio nunca se muestra — cae al siguiente campo util.
    alertSecondaryLabel(alert) {
      var d = alert && alert.data
      if (!d) return ''
      var isMeaningful = function(v) {
        if (v == null) return false
        var s = String(v).trim()
        return s !== '' && s.toUpperCase() !== 'N/A' && s !== '—'
      }
      if (alert.type === 'bus_alert') {
        var group = d.groupName || d.group || d.contact || ''
        if (isMeaningful(group)) return group
        var speed = d.speed != null ? d.speed : (d.position && d.position.speed)
        if (typeof speed === 'number' && !isNaN(speed)) return Math.round(speed) + ' km/h'
        if (isMeaningful(d.address)) return d.address
        return ''
      }
      if (alert.type === 'device_alert') {
        if (isMeaningful(d.groupName)) return d.groupName
        if (isMeaningful(d.address)) return d.address
        return ''
      }
      if (alert.type === 'external_sensor_alert') {
        if (isMeaningful(d.address)) return d.address
        var sys = d.systemName || (d.additionalData && d.additionalData.systemName)
        if (isMeaningful(sys)) return sys
        return ''
      }
      if (alert.type === 'sos' || alert.type === 'sos_call') {
        // Solo el teléfono del reportante, formateado con parsePhone (mismo
        // formato que reportantes: bandera + número nacional). El tipo de
        // llamada se muestra aparte a la derecha (alertSosCallType).
        var rawPhone = (d.userData && d.userData.phoneNumber) || d.callbackNumber || d.phone || ''
        if (!isMeaningful(rawPhone)) return ''
        var parsed = parsePhone(rawPhone)
        return parsed
          ? ((parsed.flag ? parsed.flag + ' ' : '') + (parsed.formatted || rawPhone))
          : String(rawPhone)
      }
      if (alert.type === 'hit') {
        // El arco/cámara que disparó la detección. Vive en alert.title (lo
        // arma _ingestHitAlerts desde analyticDevice.name); sin esta rama
        // caía al address —que los hits no traen— y la línea 2 iba vacía.
        var raw = d.raw || {}
        var arco = alert.title
          || (raw.analyticDevice && raw.analyticDevice.name)
          || ''
        if (isMeaningful(arco)) return arco
        if (isMeaningful(d.address)) return d.address
        return ''
      }
      // sensor_alert + incidentes + resto: address primero.
      if (isMeaningful(d.address)) return d.address
      return ''
    },

    // Match de lista negra de un hit LPR, para el chip de la línea 3. La
    // categoría trae color e ícono propios del catálogo del backend, así que
    // el chip se pinta con ellos en vez de un color fijo por tipo.
    alertBlacklist(alert) {
      if (!alert || alert.type !== 'hit' || !alert.data) return null
      var raw = alert.data.raw || {}
      var bl = raw.extendedData && raw.extendedData.blacklist
      if (!bl || typeof bl !== 'object') return null
      return {
        nombre: bl.categoryName || bl.categoryCode || bl.reason || 'Lista negra',
        color: bl.color || '',
        icon: bl.icon || 'mdi-playlist-remove',
      }
    },

    // Tipo de llamada SOS (canon) para mostrarlo a la derecha de la línea 2.
    alertSosCallType(alert) {
      var d = alert && alert.data
      if (!d) return ''
      var names = { VIDEO_CALL: 'Video llamada', CALL: 'Llamada de voz', CHAT: 'Chat' }
      return names[d.type] || ''
    },

    // Folio del incidente 9-1-1 — dato operativo clave para referenciar el
    // caso. Primario: attributes.folio (lo que el drawer muestra como "Folio");
    // fallback a uniqueId/cadId (id CAD) para que siempre haya una referencia.
    alertFolio(alert) {
      var d = alert && alert.data
      if (!d) return ''
      var raw = d.raw || d
      var attrs = raw.attributes || {}
      var folio = attrs.folio || raw.folio || raw.uniqueId || raw.cadId || ''
      return folio ? String(folio) : ''
    },
  },
}
</script>

<style scoped>
.map-sidebar {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 100%;
  background: var(--kln-bg-surface, #0d1220);
  overflow: hidden;
  pointer-events: all;
}

/* ── Toggle group (header) ── */
.sidebar-toggle-group {
  display: flex;
  gap: 2px;
  padding: 8px 8px 0;
  flex-shrink: 0;
}

.toggle-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 7px 6px;
  border: none;
  border-radius: 6px;
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.toggle-btn:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
}

.toggle-btn--active {
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
}

.toggle-badge {
  height: 16px !important;
  font-size: 9px !important;
  padding: 0 4px !important;
}

.sidebar-close-btn {
  flex-shrink: 0;
  align-self: center;
  color: var(--kln-text-muted);
  margin-left: 4px;
}

.sidebar-close-btn:hover {
  color: var(--kln-text);
}

/* ── Entity tabs: 3 chips inline + boton "+N" con dropdown ── */
.entity-tabs {
  flex-shrink: 0;
  border-bottom: 1px solid var(--kln-border-dim);
  padding: 6px 8px;
  display: flex;
  gap: 4px;
  align-items: center;
}

.entity-tabs .entity-tab {
  flex: 1 1 0;
  justify-content: center;
  min-width: 0;
}

.entity-tab--more {
  flex: 0 0 auto !important;
  background: var(--kln-border-dim) !important;
}

.entity-tab--more:hover {
  background: var(--kln-accent-soft) !important;
}

.entity-tab-count--more {
  background: transparent !important;
  color: var(--kln-text) !important;
  font-weight: 700;
}

.entity-tab {
  /* Match al patron de .alert-cat-btn para que ambos tabs tengan
     la misma altura y no haya brinco visual al cambiar de tab. */
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border: none;
  border-radius: 6px;
  background: var(--kln-border-dim);
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: all 0.15s;
}

.entity-tab:hover {
  background: var(--kln-border-dim);
}

.entity-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 16px;
  padding: 0 5px;
  font-size: 9px;
  font-weight: 700;
  color: var(--kln-text);
  line-height: 1;
  flex-shrink: 0;
}

/* ── Entity header (selected type) ── */
.entity-header {
  padding: 5px 8px 4px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--kln-border-dim);
}

.entity-header-top {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.entity-header-name {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text-muted);
  white-space: nowrap;
}

.entity-header-counts {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: var(--kln-text-muted);
  margin-left: 4px;
}

.entity-header-total {
  color: var(--kln-text-dim);
  font-size: 9px;
}

.entity-header-filters {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
}

.stats-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.stats-dot--online { background: var(--kln-status-online); }
.stats-dot--offline { background: var(--kln-status-offline); }

.stats-bar-visual {
  display: flex;
  height: 3px;
  border-radius: 2px;
  background: var(--kln-border-dim);
  overflow: hidden;
}

.stats-fill {
  height: 100%;
  transition: width 0.3s;
}

.stats-fill--online { background: var(--kln-status-online); }
.stats-fill--offline { background: var(--kln-status-offline); }

/* ── No entity selected filters ── */
.no-entity-filters {
  display: flex;
  align-items: center;
  padding: 4px 8px 0;
  flex-shrink: 0;
}

/* ── Filter dropdown ── */
.filter-dropdown {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--kln-border-dim);
  background: transparent;
  cursor: pointer;
  font-size: 10px;
  color: var(--kln-text-muted);
  white-space: nowrap;
  transition: all 0.15s;
  margin-left: auto;
}

.filter-dropdown:hover {
  border-color: var(--kln-border-normal);
  color: var(--kln-text-muted);
}

.filter-dropdown--active {
  border-color: var(--kln-border-normal);
}

.filter-dropdown-text {
  font-weight: 500;
}

.filter-dropdown-arrow {
  color: var(--kln-text-dim) !important;
}

.filter-menu {
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 6px;
  padding: 4px;
  min-width: 140px;
}

.filter-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--kln-text-muted);
  font-size: 11px;
  cursor: pointer;
  transition: background 0.1s;
}

.filter-menu-item:hover {
  background: var(--kln-border-dim);
}

.filter-menu-item--active {
  color: var(--kln-text);
  background: var(--kln-accent-soft);
}

.filter-menu-item__count {
  color: var(--kln-text-dim);
  font-size: 11px;
  margin-left: 4px;
}

/* ── Viewport toggle ── */
.viewport-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid var(--kln-border-dim);
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: var(--kln-text-dim) !important;
  transition: all 0.15s;
}

.viewport-toggle:hover {
  border-color: var(--kln-border-normal);
  color: var(--kln-text-muted) !important;
}

.viewport-toggle--active {
  background: var(--kln-accent-soft);
  border-color: var(--kln-accent);
  color: var(--kln-accent) !important;
}

/* ── Search + sort row ── */
.search-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  flex-shrink: 0;
}

.sidebar-search {
  font-size: 12px;
  flex: 1;
}

.sidebar-search--compact >>> .v-input__slot {
  min-height: 28px !important;
  padding: 0 !important;
}

.sidebar-search--compact >>> .v-input__prepend-inner {
  margin-top: 5px !important;
  padding-left: 8px !important;
}

.sidebar-search--compact >>> .v-input__append-inner {
  margin-top: 3px !important;
  padding-right: 4px !important;
}

.sidebar-search--compact >>> input {
  font-size: 11px !important;
  padding: 2px 6px !important;
}

.sort-btn {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.sort-btn--active {
  color: var(--kln-accent) !important;
}

/* ── Monitor list ── */
.monitor-list {
  flex: 1;
  overflow-y: auto;
  padding: 2px 0;
}

.monitor-list::-webkit-scrollbar {
  width: 4px;
}

.monitor-list::-webkit-scrollbar-track {
  background: transparent;
}

.monitor-list::-webkit-scrollbar-thumb {
  background: var(--kln-border-normal);
  border-radius: 2px;
}

.monitor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  cursor: pointer;
  transition: background 0.15s;
  border-left: 2px solid transparent;
}

.monitor-item:hover {
  background: var(--kln-border-dim);
}

.monitor-item--selected {
  background: var(--kln-accent-soft);
  border-left-color: var(--kln-accent);
}

.monitor-info {
  flex: 1;
  min-width: 0;
}

.monitor-title {
  font-size: 12px;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.monitor-subtitle {
  font-size: 10px;
  color: var(--kln-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.monitor-detail {
  font-size: 9px;
  color: var(--kln-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
}

.monitor-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.monitor-time {
  font-size: 10px;
  color: var(--kln-text-dim);
}

.monitor-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.monitor-status-dot--online { background: var(--kln-status-online); }
.monitor-status-dot--offline { background: var(--kln-status-offline); }
.monitor-status-dot--alert { background: var(--kln-status-alert); }
.monitor-status-dot--idle { background: var(--kln-status-idle); }

.monitor-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--kln-text-dim);
  font-size: 12px;
}

/* ── Footer ── */
.sidebar-footer {
  flex-shrink: 0;
  padding: 6px 10px;
  border-top: 1px solid var(--kln-border-dim);
  font-size: 10px;
  color: var(--kln-text-dim);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.footer-filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  background: var(--kln-border-dim);
  border-radius: 3px;
  font-size: 9px;
}

.footer-filter-clear {
  cursor: pointer;
  color: var(--kln-text-muted);
  font-size: 10px;
}

.footer-filter-clear:hover {
  color: var(--kln-alert-danger);
}

/* ── Alert category tabs ──
   Dos filas flex. Cada fila ocupa el ancho completo (chips con flex:1)
   asi los 2 chips de abajo se ven del mismo ancho que los 3 de arriba
   agrupados. Padding horizontal alineado con .sidebar-toggle-group
   (8px 8px) para que la barra completa quede justificada con el header. */
.alert-category-tabs {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 0;
  flex-shrink: 0;
}

.alert-category-tabs__row {
  display: flex;
  gap: 4px;
  width: 100%;
}

.alert-cat-btn {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 6px;
  border: none;
  border-radius: 6px;
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s;
  min-width: 0;
}

.alert-cat-btn:hover {
  background: var(--kln-border-dim);
}

.alert-cat-btn--active {
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
}

.alert-cat-icon {
  flex-shrink: 0;
}

.alert-cat-label {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.alert-cat-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 16px;
  padding: 0 5px;
  font-size: 9px;
  font-weight: 700;
  color: var(--kln-text);
  flex-shrink: 0;
}

/* ── Search action buttons (filter, sort) ── */
.search-action-btn {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.search-action-btn--active {
  color: var(--kln-accent) !important;
}

/* ── Filter popup ── */
.filter-popup {
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 10px;
  padding: 14px;
  min-width: 220px;
}

.filter-popup__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text-muted);
  margin-bottom: 10px;
}

.filter-popup__select {
  margin-bottom: 8px;
}

.filter-popup__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.filter-popup__clear {
  font-size: 12px;
  color: var(--kln-accent);
  cursor: pointer;
}

/* ── Alert list ── */
.alert-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.alert-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 12px 10px 12px;
  min-height: 52px;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid var(--kln-border-dim);
  /* Barra vertical de prioridad (solo incidents). Transparente por
     default para que alerts sin prioridad (SOS, hits, etc.) no muevan
     el layout. Las clases --priority-X sobrescriben el color. */
  border-left: 6px solid transparent;
}

.alert-item:hover {
  background: var(--kln-border-dim);
}

.alert-item--priority-alta  { border-left-color: var(--kln-alert-danger); }
.alert-item--priority-media { border-left-color: var(--kln-alert-warning); }
.alert-item--priority-baja  { border-left-color: var(--kln-alert-success); }

/* Skeleton placeholders mientras llega el primer batch del WS. Mismo
   layout que .alert-item para evitar saltos cuando se hidratan. */
.alert-skeleton-list {
  padding: 4px 0;
}
.alert-skeleton-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  min-height: 52px;
  border-bottom: 1px solid var(--kln-border-dim);
  border-left: 6px solid transparent;
}
.alert-skeleton-line {
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.10) 50%,
    rgba(255, 255, 255, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: alert-skeleton-shimmer 1.4s ease-in-out infinite;
}
.alert-skeleton-line--title { width: 70%; height: 12px; }
.alert-skeleton-line--meta  { width: 55%; height: 9px; }
.alert-skeleton-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.alert-skeleton-chip {
  width: 42px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.10) 50%,
    rgba(255, 255, 255, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: alert-skeleton-shimmer 1.4s ease-in-out infinite;
}
@keyframes alert-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Selected: la fila del alert actualmente abierto en el drawer tiene fondo
   accent-soft para que el operador no pierda contexto al alternar entre la
   vista del mapa y la lista. Matchea el look de .monitor-item--selected
   pero preserva la barra vertical de prioridad que ya le da identidad. */
.alert-item--selected {
  background: var(--kln-accent-soft);
}

.alert-item--selected:hover {
  background: var(--kln-accent-soft);
}

/* Line 1: Solo el tipo de incidente. El tiempo relativo se movió a L2
   para liberar protagonismo al título del incidente. */
.alert-line1 {
  display: flex;
  align-items: center;
  gap: 6px;
}

.alert-type-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--kln-text);
  line-height: 1.3;
}

/* Línea de folio del incidente (segunda línea) — etiqueta tenue + valor
   monospace destacado. */
.alert-folio-line {
  display: flex;
  align-items: baseline;
  overflow: hidden;
  white-space: nowrap;
}

.alert-folio-value {
  font-family: 'JetBrains Mono', 'Geist Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--kln-accent);
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Bandera de país como prefijo visual del teléfono en reportantes.
   El símbolo se genera dinámicamente desde el ISO del país detectado
   (MX, US, BR, etc.) usando Regional Indicator Symbols. El font-family
   explícito asegura que el browser use la fuente de emojis en color
   en lugar de un fallback monocromático. */
.phone-flag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 15px;
  line-height: 1;
  font-family: "Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji",
    "Noto Color Emoji", "Android Emoji", sans-serif;
}

.alert-time {
  font-size: 10px;
  color: var(--kln-text-dim);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Tipo de llamada SOS a la derecha de la línea 2 (junto al borde, como la hora). */
.alert-calltype {
  font-size: 10px;
  color: var(--kln-text-dim);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Línea 3 (solo SOS): status de la llamada (izq.) + tiempo relativo (der.). */
/* Chip de lista negra del hit. A diferencia del chip de categoría 9-1-1 (fondo
   sólido), va transparente con borde y texto en el color que manda el catálogo
   del backend: la lista de alertas ya es densa y un bloque de color por hit
   competiría con la barra de prioridad del item. */
.alert-bl-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  padding: 1px 6px;
  border: 1px solid currentColor;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.35;
  color: var(--kln-alert-danger);
}
.alert-bl-chip__icon {
  font-size: 12px !important;
  color: currentColor !important;
  flex-shrink: 0;
}
.alert-bl-chip__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alert-line3 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 1px;
  overflow: hidden;
  white-space: nowrap;
}

.alert-line3-status {
  font-size: 11px;
  color: var(--kln-text-dim);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-line3 .alert-time {
  flex-shrink: 0;
}

/* Line 2: Icon + badge + location + tiempo relativo al final. */
.alert-line2 {
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  white-space: nowrap;
}

.alert-location {
  font-size: 11px;
  color: var(--kln-text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  /* Toma el espacio restante entre el badge y el tiempo al final de la
     línea 2. El time queda pegado a la derecha con flex-shrink: 0. */
  flex: 1 1 auto;
  min-width: 0;
}

/* Icono del tipo de incidente en línea 2 — blanco neutro. Su función es
   identificar el TIPO (médico, seguridad, PC, etc.). El color de nivel
   de alerta se lee en la barra vertical izquierda del item, no en el
   icono — evita confundir los 2 canales de información. */
.alert-type-icon {
  color: var(--kln-text) !important;
  /* El glifo es lo único encogible de la línea 2 (location crece; time y el
     logo del partner ya van con shrink 0). Defensivo: que un texto largo no
     lo estreche. */
  flex-shrink: 0;
}

/* Logo del proveedor de interconexión externa en línea 2. PNG raster
   (logos de marca, no recoloreables). 16px de alto cabe sin alterar
   la altura de la línea 2, queda alineado con el icono mdi del tipo. */
.alert-partner-logo {
  height: 16px;
  width: auto;
  flex-shrink: 0;
  /* Fondo blanco solo bajo el logo — algunos PNGs (carbyne, didi)
     vienen con fondo transparente y trazo oscuro, así que un panel
     dark los hace ilegibles. El padding y border-radius dan un
     mini-chip blanco para que cualquier logo sea legible. */
  background: rgba(255, 255, 255, 0.92);
  padding: 1px 3px;
  border-radius: 2px;
  object-fit: contain;
}

/* Badge outline — DEPRECADO en MapV2 a favor del .alert-category-chip
   (chip relleno con color de la categoría CNIE en lugar de outline por
   prioridad). Se conserva para alerts no-CNIE (sos/hit/sensor) que
   todavía usan el v-icon original sin chip. */
.alert-priority {
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-size: 9px;
  letter-spacing: 0.05em;
  background: transparent;
  border: 1px solid var(--kln-border-normal);
  color: var(--kln-text-muted);
}

.alert-priority--alta  { border-color: var(--kln-alert-danger); }
.alert-priority--media { border-color: var(--kln-alert-warning); }
.alert-priority--baja  { border-color: var(--kln-alert-success); }

/* Chip de categoría CNIE (incidents 911) — reemplaza al badge ALTA/MEDIA/
   BAJA con outline. Fondo coloreado del cat-<slug> del tema activo,
   icono blanco de la categoría + texto blanco con el nombre legible.
   El nivel de prioridad sigue comunicándose por la barra vertical
   coloreada del item (.alert-item) y queda redundante en el chip
   ALTA/MEDIA/BAJA, así que lo eliminamos para no duplicar. */
.alert-category-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1.3;
}
.alert-category-chip .v-icon {
  font-size: 13px !important;
  color: #fff !important;
}
.alert-category-chip__label {
  white-space: nowrap;
}

.alert-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.alert-info {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-size: 12px;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-subtitle {
  font-size: 10px;
  color: var(--kln-text-dim);
}

.alert-time {
  font-size: 10px;
  color: var(--kln-text-dim);
  flex-shrink: 0;
}

/* ─── Scroll tabs wrapper con flechas ─────────────────────
   Layout flexbox: [flecha izq] [scroll content] [flecha der].
   Las flechas son items normales del flex; cuando aparecen
   ocupan su espacio y el scroll content se adelgaza
   automaticamente (gracias a min-width: 0 + flex: 1). NUNCA
   pasan por encima de los chips. */
.scroll-tabs-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 0 8px;
}

/* El contenido scrolleable toma todo el espacio restante
   y permite encogerse (min-width: 0 es critico para flex).
   Quitamos el padding lateral original porque el wrapper
   ahora se encarga del espacio en los bordes. */
.scroll-tabs-wrapper > .entity-tabs-scroll,
.scroll-tabs-wrapper > .alert-category-tabs {
  flex: 1;
  min-width: 0;
  padding: 0;
}

/* Las flechas son chevrons sin frame ni background — solo el
   icono visible. Asi no hay "caja" extra que pueda verse rara
   contra el glassmorphism del sidebar. Hover sutil. */
.scroll-tabs-arrow {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  flex-shrink: 0;
  width: 18px;
  height: 22px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  background: transparent;
  background-clip: padding-box;
  box-shadow: none;
  outline: none;
  font: inherit;
  color: var(--kln-text-muted);
  transition: color 0.15s, transform 0.1s;
}

.scroll-tabs-arrow > .v-icon {
  font-size: 16px !important;
  line-height: 1 !important;
  color: inherit !important;
}

.scroll-tabs-arrow:hover:not(.scroll-tabs-arrow--disabled) {
  color: var(--kln-text);
}

/* Estado disabled (en el extremo del scroll, no se puede ir mas
   alla en esa direccion). El boton se queda en su lugar para no
   cambiar el layout, pero se ve apagado y no responde a clicks. */
.scroll-tabs-arrow--disabled {
  color: var(--kln-text-dim);
  cursor: default;
  pointer-events: none;
}

.scroll-tabs-arrow:focus,
.scroll-tabs-arrow:focus-visible {
  outline: none;
  box-shadow: none;
}

.scroll-tabs-arrow::-moz-focus-inner {
  border: 0;
  padding: 0;
}

.scroll-tabs-arrow:active {
  transform: scale(0.85);
}
</style>

<style>
/* No-scoped: v-menu content se porta fuera del arbol del componente,
   asi que los selectores scoped no alcanzan. */
.entity-overflow-menu {
  background: var(--kln-bg-surface, #0d1220) !important;
  border: 1px solid var(--kln-border-normal);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  max-height: 360px;
  overflow-y: auto;
  min-width: 220px;
}

.entity-overflow-menu__list {
  padding: 4px;
}

.entity-overflow-menu__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--kln-text);
  cursor: pointer;
  transition: background 0.12s;
}

.entity-overflow-menu__item:hover {
  background: var(--kln-border-dim);
}

.entity-overflow-menu__label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.entity-overflow-menu__count {
  min-width: 22px;
  height: 16px;
  padding: 0 6px;
  color: var(--kln-text-muted);
  font-size: 9px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
